import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';
const thresholdCents = 7500;
let mutationInProgress = false;

async function requestCart(path, payload) {
  const response = await fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error('cart request failed');
  return response.json();
}

async function getCart() {
  const response = await fetch(`${root}cart.js`);
  if (!response.ok) throw new Error('cart request failed');
  return response.json();
}

function sectionIds() {
  return [...new Set(
    [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
  )];
}

function updateTheme(cart, sections, action, variantId) {
  document.dispatchEvent(new CartLinesUpdateEvent({
    action,
    context: 'cart',
    lines: [{ id: String(variantId), quantity: action === 'remove' ? 0 : 1 }],
    promise: Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'gift-drawer-promo',
        didError: false,
      },
    }),
  }));
}

function qualifiedSubtotal(cart) {
  return cart.items.reduce((total, item) => {
    return total + (item.properties?._free_gift === 'true' ? 0 : item.original_line_price);
  }, 0);
}

function selectedGift(cart) {
  return cart.items.find((item) => item.properties?._free_gift === 'true');
}

function sectionPayload() {
  return { sections: sectionIds().join(','), sections_url: window.location.pathname };
}

async function removeInvalidGift() {
  if (mutationInProgress || !document.querySelector('[data-gift-drawer-promo], [data-free-gift-picker]')) return;
  mutationInProgress = true;
  try {
    const cart = await getCart();
    const gift = selectedGift(cart);
    if (!gift || qualifiedSubtotal(cart) >= thresholdCents) return;

    const result = await requestCart('cart/change.js', {
      id: gift.key,
      quantity: 0,
      ...sectionPayload(),
    });
    updateTheme(await getCart(), result.sections, 'remove', gift.variant_id);
    if (window.location.pathname.replace(/\/$/, '') === `${root}cart`.replace(/\/$/, '')) {
      window.location.reload();
    }
  } catch (error) {
    console.warn('[gift-drawer-promo] Could not remove an ineligible gift:', error);
  } finally {
    mutationInProgress = false;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-gift-choose]');
  if (!button || mutationInProgress) return;

  const promo = button.closest('[data-gift-drawer-promo]');
  const choice = promo?.querySelector('[data-gift-choice]');
  const error = promo?.querySelector('[data-gift-error]');
  const variantId = choice?.value;
  if (!variantId) {
    if (error) {
      error.textContent = 'Bitte wähle zuerst ein Geschenk aus.';
      error.hidden = false;
    }
    return;
  }

  mutationInProgress = true;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (error) error.hidden = true;

  let previousGift;
  let removedPreviousGift = false;
  try {
    const cart = await getCart();
    if (qualifiedSubtotal(cart) < thresholdCents) {
      throw new Error('threshold not met');
    }
    previousGift = selectedGift(cart);
    if (String(previousGift?.variant_id) === String(variantId)) return;

    if (previousGift) {
      await requestCart('cart/change.js', { id: previousGift.key, quantity: 0 });
      removedPreviousGift = true;
    }

    const result = await requestCart('cart/add.js', {
      items: [{ id: Number(variantId), quantity: 1, properties: { _free_gift: 'true' } }],
      ...sectionPayload(),
    });
    updateTheme(await getCart(), result.sections, 'add', variantId);
  } catch (failure) {
    if (removedPreviousGift && previousGift) {
      try {
        await requestCart('cart/add.js', {
          items: [{ id: previousGift.variant_id, quantity: 1, properties: { _free_gift: 'true' } }],
        });
      } catch (restoreFailure) {
        console.warn('[gift-drawer-promo] Could not restore the previous gift:', restoreFailure);
      }
    }
    if (error) {
      error.textContent = 'Das Geschenk konnte nicht hinzugefügt werden. Bitte versuche es erneut.';
      error.hidden = false;
    }
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    mutationInProgress = false;
  }
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(({ detail }) => {
    if (detail?.source !== 'gift-drawer-promo') void removeInvalidGift();
  }).catch(() => {});
});

void removeInvalidGift();
